import type { CreatorSDKConfig } from '@creator/sdk/creator-sdk';
import type { BackedToken } from '@creator/sdk/modules/token/token.model';
import { getConfig } from '../config/index';
import { isLocalHost } from './utils/url-utils/url-utils';

// Load the unified configuration
const config = getConfig();

// Extract domain name with fallback logic for backward compatibility
const domainName = config.domainName ? config.domainName : (window.location.host.indexOf('www.') && window.location.host) || window.location.host.replace('www.', '');

export function getTransakApiKey(): string {
    return config.transakApiKey;
}

export function getTransakWidgetBaseUrl(): string {
    return config.transakWidgetBaseUrl;
}

export function getMoonpayApiKey(): string {
    return config.moonpayApiKey;
}

export function getBackedToken(): BackedToken {
    return config.backedToken;
}

export function getBountyToken(): BackedToken {
    return config.bountyToken;
}

export function getMoonpayWidgetBaseUrl(): string {
    return config.moonpayWidgetBaseUrl;
}

export function getFiatInWalletAddress(): string {
    return config.contractName;
}

export function getStakingTokenPageRedirect(): string {
    return config.stakingTokenPageRedirect || 'RTBHOME';
}

export function getFiatInProviders(): string[] {
    return config.fiatInProviders;
}

export function getBaseUrl(): string {
    return window.location.origin;
}

export function getFunctionsBaseUrl() {
    const { functionsBaseUrl } = config;
    // return 'http://127.0.0.1:5001/creator-eco-stage/us-central1';
    // return 'http://localhost:5000/creator-eco-stage/us-central1';
    return functionsBaseUrl;
}

export function getFrontendFunctionsBaseUrl() {
    // return 'http://localhost:5000/creator-eco-stage/us-central1/mainWebpageV2/api';
    const { frontendFunctionsBaseUrl } = config;
    return frontendFunctionsBaseUrl;
}

export function getCloudRunBaseUrl() {
    const { cloudRunBaseUrl } = config;
    return cloudRunBaseUrl;
}

export function getRpcBaseUrl() {
    const { rpcBaseUrl } = config;
    return rpcBaseUrl;
}

export function getCreatorSdkConfig(): CreatorSDKConfig {
    const enableAnalytics = !['thehockeynews.com', 'creator-eco-stage.web.app', 'warriormaven.com', 'thecurlingnews.com'].includes(window.location.hostname);

    return {
        operatorName: getOperatorName(),
        domainName: getDomainName(),
        operatorSalt: config.operatorSalt,
        rpcBaseUrl: config.rpcBaseUrl,
        contractName: config.contractName,
        registryContractName: config.registryContractName,
        cloudRunBaseUrl: config.cloudRunBaseUrl,
        functionsBaseUrl: config.functionsBaseUrl,
        firebaseConfig: config.firebaseConfig,
        enableAnalytics,
        appCheckRecaptchaKey: config.appCheckRecaptchaKey,
        enableAppCheckDebugToken: isLocalHost(),
        upvote: {
            contractName: config.upvote.contractName,
        },
    };
}

export function getContracetName(): string {
    return config.contractName;
}

export function getFirebaseStorageBucket(): string {
    const { firebaseConfig } = config;
    return firebaseConfig.storageBucket;
}

export function getOperatorName(): string {
    return config.operatorName;
}

export function getCoinbaseWithdrawToEosAddress(): string {
    return 'coinbasebase';
}

export function getDomainName() {
    return config.domainName;
}

export function getImageMaxUploadFileSize(): number {
    // in byes
    return 10 * 1024 * 1024; // 10MB
}

export function getVideoMaxUploadFileSize(): number {
    // in byes
    return 2000 * 1024 * 1024; // 2GB
}

export function getAudioMaxUploadFileSizeMobile(): number {
    // in byes
    return 500 * 1024 * 1024; // 500MB
}

export function getAudioMaxUploadFileSize(): number {
    // in byes
    return 500 * 1024 * 1024; // 500MB
}

export function getFacebookAppId(): string {
    return config.facebookAppId;
}

export function getAcceptedImagesForUpload(): string[] {
    return ['image/png', 'image/gif', 'image/jpeg', 'image/tiff', 'image/bmp', 'image/webp', 'image/avif'];
}

export function getAcceptedVideosForUpload(): string[] {
    return [
        '.mkv', // MKV
        '.avi',
        'video/mp4', // MP4
        'video/x-matroska', // MKV
        'video/x-msvideo', // AVI
        'video/quicktime', // MOV
        'video/x-flv', // FLV
        'video/x-ms-wmv', // WMV
        'video/mpeg', // MPG, MPEG
        'video/x-m4v', // M4V
        'video/webm', // WebM
    ];
}

export function getAcceptedAudioForUpload(): string[] {
    return ['audio/mpeg', 'audio/aac', 'audio/wav', 'audio/ogg', 'audio/x-m4a'];
}

export function getGoogleProviderClientId(): string {
    return config.googleProviderClientId;
}

export function getOperatorMobileApp() {
    return config.mobileApp;
}

export function getCdnStorageBaseUrl() {
    return config.cdn.storageBaseUrl;
}

export function getFirebaseProjectId(): string {
    const { firebaseConfig } = config;
    return firebaseConfig.projectId;
}
